import React from 'react';
import {NavLink, Link, withRouter} from 'react-router-dom';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faCog} from '@fortawesome/pro-light-svg-icons/faCog';
import {faCogs} from '@fortawesome/pro-light-svg-icons/faCogs';
import {faLock} from '@fortawesome/pro-light-svg-icons/faLock';
import {faBell} from '@fortawesome/pro-light-svg-icons/faBell';
import {faLifeRing} from '@fortawesome/pro-light-svg-icons/faLifeRing';
import {faFilePdf} from '@fortawesome/pro-light-svg-icons/faFilePdf';
import {faBullseye} from '@fortawesome/pro-solid-svg-icons/faBullseye'

import Base from 'Components/Base/Base';

import Notifications from './Notifications';
import VersionNotes from './VersionNotes';

import {Scopes, Modal} from 'Services';
import Version from 'Services/Api/Jumbo/Version';

import adminScopes from 'Config/Scopes/Admin';
import { faBallotCheck } from "@fortawesome/pro-solid-svg-icons/faBallotCheck";
import { faIslandTropical } from "@fortawesome/pro-light-svg-icons/faIslandTropical";
import { faHeadSideCough } from "@fortawesome/pro-light-svg-icons/faHeadSideCough";
import { faDownload } from "@fortawesome/pro-regular-svg-icons/faDownload";

/**
 * @classdesc Top navigation bar for
 */
class TopNav extends Base {
    /**
     * @var openRef
     * @type {HTMLDivElement}
     */
    openRef = null;

    /**
     * @var state
     */
    state = {
        showUserMenu: false,
        showNotifications: false,
        stats: {
            notifications: 0,
        },
        version: window.appBase?.version ?? '',
        newVersion: false
    };

    /**
     * @method componentDidMount
     */
    componentDidMount = () => {
        this.fetchVersion();

        document.addEventListener('mousedown', this.handleDismissNotifications);
        document.addEventListener('touchstart', this.handleDismissNotifications);
    };

    /**
     * @method componentWillUnmount
     */
    componentWillUnmount = () => {
        document.removeEventListener('mousedown', this.handleDismissNotifications);
        document.removeEventListener('touchstart', this.handleDismissNotifications);

        clearTimeout(window.versionTimeout);
    };

    /**
     * @method handleDismissNotifications
     */
    handleDismissNotifications = (event) => {
        if (!this.openRef || this.openRef.contains(event.target)) {
            return;
        }

        this.setState({
            showNotifications: false,
            showUserMenu: false,
        });
    }

    /**
     * @method fetchVersion
     * @return {Promise<void>}
     */
    fetchVersion = async () => {
        window.versionTimeout = setTimeout(async () => {
            clearTimeout(window.versionTimeout);

            const {version} = this.state;
            const request = await Version.fetch();

            if (request.status !== 200) {
                return this.defaultToastrError();
            }

            if (version === null) {
                this.setState({
                    version: request.data.data.version
                });
            } else {
                if (request.data.data.version !== version) {
                    this.setState({
                        newVersion: true
                    });
                }
            }
        }, 15 * 60 * 1000);
    };

    /**
     * @method handleToggleUserMenu
     */
    handleToggleUserMenu = () => this.setState({
        showUserMenu: !this.state.showUserMenu,
    });

    /**
     * @method handleToggleNotifications
     */
    handleToggleNotifications = () => this.setState({
        showNotifications: !this.state.showNotifications,
    });

    /**
     * @method handleOpenVersionNotes
     */
    handleOpenVersionNotes = () => Modal.open('VersionNotes', {
        component: VersionNotes,
    });

    /**
     * @method handleRefresh
     */
    handleRefresh = () => window.location.reload();

    /**
     * @method render
     * @return {*}
     */
    render() {
        const {showUserMenu, showNotifications, version, newVersion} = this.state;
        const {user, handleToggleCollapse, notifications} = this.props;

        return (
            <React.Fragment>
                {newVersion && this.renderNewVersionNotice()}

                <div
                    className="relative z-10 shrink-0 flex h-12 bg-white shadow">
                    <button
                        onClick={handleToggleCollapse}
                        className={
                            'px-4 border-r border-gray-200 ' +
                            'focus:outline-none focus:bg-gray-100 focus:text-gray-600'
                        }
                        aria-label="Open sidebar"
                    >
                        <svg
                            className="h-6 w-6"
                            stroke="currentColor"
                            fill="none"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M4 6h16M4 12h16M4 18h7"
                            />
                        </svg>
                    </button>
                    <div className="flex-1 px-4 flex justify-between">
                        <div>
                            <NavLink to="/">
                                <img src={'/images/tuthill-logo.png'} className="h-8 mt-2"/>
                            </NavLink>
                        </div>

                        <div className="ml-4 flex items-center md:ml-6">
                            {process.env.MIX_APP_ENV !== 'production' && (
                                <div className="p-1 font-bold text-red-500 uppercase">
                                    Env: {process.env.MIX_APP_ENV}
                                </div>
                            )}

                            <div className="p-1 mr-2 text-sm text-app-blue-light hidden sm:block">
                                You are signed in as: {user.full_name}
                            </div>

                            <div
                                className="p-1 mr-2 text-sm cursor-pointer hidden sm:block"
                                onClick={this.handleOpenVersionNotes}
                            >
                                Version: {version}
                            </div>

                            {Scopes.has('duality-studio.support') && (
                                <a href="/support" target="_blank" className="p-1 mr-2 text-gray-400 rounded-full hover:bg-gray-100 focus:outline-none focus:shadow-outline">
                                    <FontAwesomeIcon icon={faLifeRing} size="lg"/>
                                </a>
                            )}

                            {Scopes.has(adminScopes) && (
                                <NavLink
                                    to="/admin"
                                    className={
                                        'p-1 mr-2 text-gray-400 rounded-full hover:bg-gray-100 ' +
                                        'focus:outline-none focus:shadow-outline '
                                    }
                                >
                                    <FontAwesomeIcon icon={faCogs} size="lg"/>
                                </NavLink>
                            )}

                            <div className="relative">
                                <button
                                    className={
                                        'p-1 mr-2 text-gray-400 rounded-full hover:bg-gray-100 ' +
                                        'hover:text-gray-500 focus:outline-none ' +
                                        'focus:shadow-outline focus:text-gray-500'
                                    }
                                    onClick={this.handleToggleNotifications}
                                >
                                    <span className="fa-layers fa-fw">
                                        <FontAwesomeIcon icon={faBell} size="lg"/>

                                        {notifications >= 1 && (
                                            <span
                                                className="fa-layers-counter fa-3x bg-app-red-bright"
                                                style={{
                                                    margin: '-12px -12px 0 0',
                                                }}
                                            >
                                                {notifications}
                                            </span>
                                        )}
                                    </span>
                                </button>

                                {showNotifications && (
                                    <div ref={ref => this.openRef = ref}>
                                        <Notifications
                                            user={this.props.user}
                                            onClose={this.handleToggleNotifications}
                                        />
                                    </div>
                                )}

                                <NavLink
                                    to="/downloads"
                                    className={
                                        'p-1 mr-2 text-gray-400 rounded-full hover:bg-gray-100 ' +
                                        'hover:text-gray-500 focus:outline-none ' +
                                        'focus:shadow-outline focus:text-gray-500'
                                    }
                                >
                                    <FontAwesomeIcon icon={faDownload} />
                                </NavLink>
                            </div>

                            <div className="ml-3 relative">
                                <div>
                                    <button
                                        className={
                                            'max-w-xs flex items-center text-sm rounded-full ' +
                                            'focus:outline-none focus:shadow-outline'
                                        }
                                        id="user-menu"
                                        onClick={this.handleToggleUserMenu}
                                    >
                                        <img
                                            className="h-8 w-8 rounded-full"
                                            src={user.avatar_url}
                                            alt={user.first_name}
                                        />
                                    </button>
                                </div>

                                {showUserMenu && (
                                    <div
                                        ref={ref => this.openRef = ref}
                                        className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg"
                                    >
                                        <div
                                            className="py-1 rounded-md bg-white shadow-xs divide-y divide-gray-100"
                                            role="menu"
                                        >
                                            {Scopes.has(['hr.has-documents', 'hr.has-objectives']) && (
                                                <div>
                                                    {Scopes.has('tasks.*') && (
                                                        <NavLink
                                                            className={
                                                                'block px-4 py-2 text-sm text-gray-700 ' +
                                                                'hover:bg-gray-100 transition ease-in-out duration-150'
                                                            }
                                                            activeClassName=""
                                                            to="/to-do"
                                                        >
                                                            <FontAwesomeIcon
                                                                icon={faBallotCheck}
                                                                className="mr-2"
                                                                fixedWidth
                                                            />
                                                            My Tasks
                                                        </NavLink>
                                                    )}

                                                    {Scopes.has('holiday.create') && (
                                                        <NavLink
                                                            className={
                                                                'block px-4 py-2 text-sm text-gray-700 ' +
                                                                'hover:bg-gray-100 transition ease-in-out duration-150'
                                                            }
                                                            activeClassName=""
                                                            to="/my-holidays"
                                                        >
                                                            <FontAwesomeIcon
                                                                icon={faIslandTropical}
                                                                className="mr-2"
                                                                fixedWidth
                                                            />
                                                            My Leave
                                                        </NavLink>
                                                    )}

                                                    {Scopes.has('absence.create') && (
                                                        <NavLink
                                                            className={
                                                                'block px-4 py-2 text-sm text-gray-700 ' +
                                                                'hover:bg-gray-100 transition ease-in-out duration-150'
                                                            }
                                                            activeClassName=""
                                                            to="/my-absences"
                                                        >
                                                            <FontAwesomeIcon
                                                                icon={faHeadSideCough}
                                                                className="mr-2"
                                                                fixedWidth
                                                            />
                                                            My Absences
                                                        </NavLink>
                                                    )}

                                                    {Scopes.has('hr.has-documents') && (
                                                        <NavLink
                                                            className={
                                                                'block px-4 py-2 text-sm text-gray-700 ' +
                                                                'hover:bg-gray-100 transition ease-in-out duration-150'
                                                            }
                                                            activeClassName=""
                                                            to="/settings/documents"
                                                        >
                                                            <FontAwesomeIcon
                                                                icon={faFilePdf}
                                                                className="mr-2"
                                                                fixedWidth
                                                            />
                                                            My Documents
                                                        </NavLink>
                                                    )}

                                                    {Scopes.has('hr.has-objectives') && (
                                                        <NavLink
                                                            className={
                                                                'block px-4 py-2 text-sm text-gray-700 ' +
                                                                'hover:bg-gray-100 transition ease-in-out duration-150'
                                                            }
                                                            activeClassName=""
                                                            to="/settings/objectives"
                                                        >
                                                            <FontAwesomeIcon
                                                                icon={faBullseye}
                                                                className="mr-2"
                                                                fixedWidth
                                                            />
                                                            My Objectives
                                                        </NavLink>
                                                    )}
                                                </div>
                                            )}

                                            <div>
                                                <NavLink
                                                    className={
                                                        'block px-4 py-2 text-sm text-gray-700 ' +
                                                        'hover:bg-gray-100 transition ease-in-out duration-150'
                                                    }
                                                    activeClassName=""
                                                    to="/settings/profile"
                                                >
                                                    <FontAwesomeIcon
                                                        icon={faCog}
                                                        className="mr-2"
                                                        fixedWidth
                                                    />
                                                    Settings
                                                </NavLink>

                                                <NavLink
                                                    className={
                                                        'block px-4 py-2 text-sm text-gray-700 ' +
                                                        'hover:bg-gray-100 transition ease-in-out duration-150'
                                                    }
                                                    to="/logout"
                                                >
                                                    <FontAwesomeIcon
                                                        icon={faLock}
                                                        fixedWidth
                                                        className="mr-2"
                                                    />
                                                    Logout
                                                </NavLink>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </React.Fragment>
        );
    };

    /**
     * @method renderNewVersionNotice
     * @return {JSX.Element}
     */
    renderNewVersionNotice = () => {
        return (
            <div
                onClick={this.handleRefresh}
                className="bg-red-500 text-white py-3 text-center hover:bg-red-300 cursor-pointer transition duration-200"
            >
                <p>There's a new version available! Click here to check it out</p>
            </div>
        );
    };
}

export default withRouter(TopNav);
